import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useWS } from "@/contexts/WebSocketContext";
import { AlertTriangle, Clock, ChefHat, AlarmClock, Archive, Check, Play, Hand, BellRing, Wine, Pizza, Printer, Tv, Layers, CheckCircle2, ArrowDownCircle, X } from "lucide-react";
import { beepWarning20, alarmCritical30, softClick, bellCustomerCall } from "@/lib/audioAlerts";
import { toast } from "sonner";

const elapsed = (iso) => {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return "ora";
    return `${m}'`;
};

const countdown = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "0:00";
    const total_s = Math.floor(ms / 1000);
    const m = Math.floor(total_s / 60);
    const s = total_s % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
};

const STATION_META = {
    kitchen:  { title: "Terminale Cucina",   icon: ChefHat, label: "Cucina" },
    bar:      { title: "Terminale Bar",      icon: Wine,    label: "Bar" },
    pizza:    { title: "Terminale Pizzeria", icon: Pizza,   label: "Pizzeria" },
};

const MODE_META = {
    print_only: { label: "Solo stampante", icon: Printer, color: "text-orange-300", desc: "Comande stampate. Item subito pronti, KDS in sola lettura." },
    mixed:      { label: "Misto",          icon: Layers,  color: "text-purple-300", desc: "Comande stampate + visibili a video. Item subito pronti, KDS in sola lettura. Resta disponibile 'Chiama cameriere'." },
    video_only: { label: "Solo video",     icon: Tv,      color: "text-cyan-300",   desc: "Solo KDS video. Nessuna stampa. Il cuoco gestisce gli stati con i pulsanti." },
};

const TerminaleCucina = ({ station = "kitchen" }) => {
    const meta = STATION_META[station] || STATION_META.kitchen;
    const HeaderIcon = meta.icon;
    const [tickets, setTickets] = useState([]);
    const [mode, setMode] = useState("video_only");
    const [modeSaving, setModeSaving] = useState(false);
    const { lastEvent } = useWS();
    const navigate = useNavigate();
    const alertedRef = useRef({ yellow: new Set(), red: new Set() });
    // ⚠️ Alert "ARRIVATA NUOVA COMANDA" — solo in video_only
    const [newOrderAlert, setNewOrderAlert] = useState(null); // { orderId, tableNumber }
    const ticketRefs = useRef(new Map());

    const load = async () => {
        const { data } = await api.get(`/kitchen/tickets?station=${station}`);
        setTickets(data);
    };

    const loadMode = async () => {
        try { const { data } = await api.get("/station-settings"); setMode(data[station] || "video_only"); }
        catch (_e) { /* */ }
    };

    useEffect(() => { load(); loadMode(); const id = setInterval(load, 3000); return () => clearInterval(id); }, [station]);

    // Tick ogni secondo per aggiornare i countdown in print/mixed mode
    const [, setNowTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setNowTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, []);
    useEffect(() => {
        if (["new_kitchen_order", "item_ready", "item_delivered", "order_items_ready"].includes(lastEvent?.type)) load();
        if (lastEvent?.type === "station_mode_changed" && lastEvent?.station === station) setMode(lastEvent.mode);
        // ⚠️ Alert "ARRIVATA NUOVA COMANDA" — appare SOLO in modalità video_only
        if (lastEvent?.type === "new_kitchen_order" && mode === "video_only" && lastEvent.order_id) {
            beepWarning20(); // suono di richiamo cucina
            setNewOrderAlert({ orderId: lastEvent.order_id });
            // Auto-dismiss dopo 12s se l'utente non clicca
            const tm = setTimeout(() => setNewOrderAlert(null), 12000);
            return () => clearTimeout(tm);
        }
    }, [lastEvent, mode]);

    const scrollToOrder = (orderId) => {
        setNewOrderAlert(null);
        const el = ticketRefs.current.get(orderId);
        if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            // Flash highlight per 2s
            el.classList.add("ring-4", "ring-cyan-400", "ring-offset-2", "ring-offset-deep");
            setTimeout(() => {
                el.classList.remove("ring-4", "ring-cyan-400", "ring-offset-2", "ring-offset-deep");
            }, 2000);
        }
    };

    // EVASA: marca comanda evasa → cancella tile dopo 30s + suona tablet cameriere
    const markEvasa = async (ticket) => {
        try {
            softClick();
            await api.post(`/orders/${ticket.id}/evasa`, {});
            toast.success(`Evasa: ${ticket.table_number ? `tavolo ${ticket.table_number}` : ""} — tile sparirà tra 30s`, { duration: 3000 });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore segnalazione evasa");
        }
    };

    const changeMode = async (newMode) => {
        if (newMode === mode) return;
        setModeSaving(true);
        try {
            await api.patch(`/station-settings/${station}`, { mode: newMode });
            setMode(newMode);
            toast.success(`Modalità ${meta.label} aggiornata: ${MODE_META[newMode].label}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally {
            setModeSaving(false);
        }
    };

    // Allarmi audio sui ritardi: ogni ticket suona UNA volta al passaggio 20' (giallo) e UNA volta a 30' (rosso).
    useEffect(() => {
        tickets.forEach((t) => {
            if (t.alert_level === "red" && !alertedRef.current.red.has(t.id)) {
                alertedRef.current.red.add(t.id);
                alertedRef.current.yellow.add(t.id); // sopprime un eventuale beep giallo successivo
                alarmCritical30();
            } else if (t.alert_level === "yellow" && !alertedRef.current.yellow.has(t.id)) {
                alertedRef.current.yellow.add(t.id);
                beepWarning20();
            }
        });
        // Pulisci ticket scomparsi (consegnati/chiusi) per permettere nuovi alert futuri
        const liveIds = new Set(tickets.map((t) => t.id));
        for (const id of [...alertedRef.current.yellow]) if (!liveIds.has(id)) alertedRef.current.yellow.delete(id);
        for (const id of [...alertedRef.current.red])    if (!liveIds.has(id)) alertedRef.current.red.delete(id);
    }, [tickets]);

    const setStatus = async (orderId, itemId, status) => {
        try {
            softClick();
            await api.post(`/orders/${orderId}/items/${itemId}/status`, { status });
            load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Operazione non possibile";
            toast.error(msg, { duration: 6000 });
            console.warn("[setStatus]", msg);
        }
    };

    const callWaiter = async (ticket) => {
        try {
            bellCustomerCall();
            await api.post("/kitchen/call-waiter", { table_id: ticket.table_id });
            toast.success(`Cameriere chiamato per tavolo ${ticket.table_number}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore chiamata cameriere");
        }
    };

    const yellowCount = tickets.filter((t) => t.alert_level === "yellow").length;
    const redCount = tickets.filter((t) => t.alert_level === "red").length;

    return (
        <div className="space-y-6" data-testid={`station-${station}-page`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">KDS · {meta.label}</div>
                    <h1 className="font-display text-4xl font-bold flex items-center gap-3">
                        <HeaderIcon className="w-9 h-9 text-brand" /> {meta.title}
                    </h1>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {redCount > 0 && (
                        <div className="px-3 h-10 bg-red-900/40 border border-red-600 rounded-md flex items-center gap-2 text-red-300 text-sm font-bold animate-pulse" data-testid="alert-red">
                            <AlarmClock className="w-4 h-4" /> {redCount} {redCount === 1 ? "ritardo critico" : "ritardi critici"} (&gt;30')
                        </div>
                    )}
                    {yellowCount > 0 && (
                        <div className="px-3 h-10 bg-amber-900/40 border border-amber-600 rounded-md flex items-center gap-2 text-amber-300 text-sm font-bold" data-testid="alert-yellow">
                            <AlertTriangle className="w-4 h-4" /> {yellowCount} {yellowCount === 1 ? "in ritardo" : "in ritardo"} (&gt;20')
                        </div>
                    )}
                    <div className="text-elegant text-sm">{tickets.length} comande attive</div>
                    <button onClick={() => navigate("/comande-archiviate")}
                        data-testid="archived-orders-button"
                        className="h-10 px-4 border border-brand text-brand rounded-md flex items-center gap-2 hover:bg-brand-soft text-sm uppercase tracking-wider">
                        <Archive className="w-4 h-4" /> Archivio
                    </button>
                </div>
            </div>

            {/* Mode toggle */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-elegant mb-1">Modalità di lavoro {meta.label}</div>
                        <div className="text-sm text-warm">{MODE_META[mode].desc}</div>
                    </div>
                    <div className="inline-flex bg-deep border border-[#1f2c44] rounded-lg p-1" data-testid={`mode-toggle-${station}`}>
                        {Object.entries(MODE_META).map(([k, m]) => {
                            const Icon = m.icon;
                            const active = mode === k;
                            return (
                                <button key={k} onClick={() => changeMode(k)} disabled={modeSaving}
                                    data-testid={`mode-${station}-${k}`}
                                    className={`h-10 px-4 rounded-md text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 transition-colors disabled:opacity-50 ${
                                        active ? "bg-brand text-deep" : "text-elegant hover:text-warm"
                                    }`}>
                                    <Icon className="w-3.5 h-3.5" />
                                    {m.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {mode === "print_only" && (
                <div className="bg-orange-900/15 border-2 border-orange-600/40 rounded-lg p-6 text-center" data-testid={`print-only-banner-${station}`}>
                    <Printer className="w-10 h-10 text-orange-300 mx-auto mb-2" />
                    <div className="font-display text-xl font-bold text-warm mb-1">Modalità Solo Stampante attiva</div>
                    <div className="text-sm text-elegant">
                        Le comande vengono stampate per intero subito e scompaiono automaticamente dopo <strong className="text-orange-300">5 minuti</strong>.<br />
                        Nessuna conferma richiesta. Qui sotto vedi le comande in corso per consultazione.
                    </div>
                </div>
            )}

            {mode === "mixed" && (
                <div className="bg-purple-900/15 border-2 border-purple-600/40 rounded-lg p-4 text-center" data-testid={`mixed-banner-${station}`}>
                    <div className="font-display text-base font-bold text-warm mb-0.5 flex items-center justify-center gap-2">
                        <Layers className="w-5 h-5 text-purple-300" /> Modalità Misto attiva
                    </div>
                    <div className="text-xs text-elegant">
                        Stampa intera + video. Premi <strong className="text-emerald-300">EVASA</strong> quando il piatto è pronto: il cameriere sarà avvisato sul tablet. Auto-archivio dopo <strong className="text-purple-300">1 minuto</strong>.
                    </div>
                </div>
            )}

            {/* ⚠️ Alert "ARRIVATA NUOVA COMANDA" — solo in video_only */}
            {mode === "video_only" && newOrderAlert && (
                <div className="sticky top-2 z-30 bg-cyan-900/90 backdrop-blur border-2 border-cyan-400 rounded-lg p-4 flex items-center gap-3 shadow-xl animate-pulse" data-testid="new-order-alert">
                    <BellRing className="w-7 h-7 text-cyan-300 flex-shrink-0" />
                    <div className="flex-1">
                        <div className="font-display text-lg font-bold text-cyan-100 uppercase tracking-wider">Arrivata nuova comanda</div>
                        <div className="text-xs text-cyan-200/80">Una comanda è appena entrata in cucina</div>
                    </div>
                    <button
                        onClick={() => scrollToOrder(newOrderAlert.orderId)}
                        data-testid="new-order-alert-goto"
                        className="h-10 px-4 bg-cyan-400 text-deep font-bold uppercase tracking-wider rounded-md flex items-center gap-1.5 hover:bg-cyan-300 text-xs"
                    >
                        <ArrowDownCircle className="w-4 h-4" /> Vai alla comanda
                    </button>
                    <button
                        onClick={() => setNewOrderAlert(null)}
                        data-testid="new-order-alert-dismiss"
                        className="h-10 w-10 border border-cyan-400/40 rounded-md text-cyan-200 hover:bg-cyan-800/40 flex items-center justify-center"
                        title="Chiudi"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {tickets.length === 0 && mode !== "print_only" && (
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-12 text-center text-elegant">
                    Nessuna comanda in attesa. Buon servizio!
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {tickets.map((t) => {
                    const alertBorder =
                        t.alert_level === "red" ? "border-red-600 ring-2 ring-red-600/40 animate-pulse" :
                        t.alert_level === "yellow" ? "border-amber-600 ring-1 ring-amber-600/40" :
                        "border-[#1f2c44]";
                    const waitColor =
                        t.alert_level === "red" ? "text-red-400" :
                        t.alert_level === "yellow" ? "text-amber-300" :
                        t.wait_minutes >= 10 ? "text-amber-400" : "text-green-400";
                    return (
                        <div
                            key={t.id}
                            ref={(el) => { if (el) ticketRefs.current.set(t.id, el); else ticketRefs.current.delete(t.id); }}
                            className={`bg-surface border-2 rounded-lg overflow-hidden flex flex-col transition-shadow ${alertBorder}`}
                            data-testid={`ticket-${t.id}`}
                        >
                            <div className="px-4 py-3 bg-gradient-to-r from-[#182337] to-[#111c2e] border-b border-[#1f2c44] flex justify-between items-center gap-2">
                                <div className="flex-shrink-0">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-brand">Tavolo</div>
                                    <div className="font-display text-3xl font-bold leading-tight">{t.table_number}</div>
                                    {t.room_name && (
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-elegant mt-0.5" data-testid={`ticket-room-${t.id}`}>
                                            {t.room_name}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => callWaiter(t)}
                                    data-testid={`call-waiter-${t.id}`}
                                    title="Chiama cameriere per questo tavolo"
                                    className="h-10 px-3 rounded-md border border-purple-500 bg-purple-900/30 text-purple-200 hover:bg-purple-800/50 active:scale-95 transition-all flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider"
                                >
                                    <BellRing className="w-4 h-4" /> Chiama cameriere
                                </button>
                                <div className="text-right flex-shrink-0">
                                    <div className={`flex items-center gap-1 font-display font-bold text-2xl ${waitColor}`}>
                                        <Clock className="w-4 h-4" /> {t.wait_minutes ?? 0}'
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-elegant mt-1">{t.waiter_name}</div>
                                    {t.alert_level === "red" && (
                                        <div className="text-[10px] font-bold text-red-300 uppercase tracking-wider mt-1">⚠ critico</div>
                                    )}
                                    {t.alert_level === "yellow" && (
                                        <div className="text-[10px] font-bold text-amber-300 uppercase tracking-wider mt-1">⚠ ritardo</div>
                                    )}
                                </div>
                            </div>
                            <div className="p-3 space-y-3 flex-1">
                                {(() => {
                                    const cOrder = ["antipasto", "primo", "secondo", "contorno", "pizza", "dolce", "bevanda"];
                                    const grouped = {};
                                    t.items.forEach((it) => {
                                        const r = it.course_round || 1;
                                        if (!grouped[r]) grouped[r] = [];
                                        grouped[r].push(it);
                                    });
                                    const sortedRounds = Object.keys(grouped).map(Number).sort((a, b) => a - b);
                                    return sortedRounds.map((r) => {
                                        const roundItems = grouped[r].sort((a, b) => cOrder.indexOf(a.course) - cOrder.indexOf(b.course));
                                        const isWaitingRound = roundItems.every((i) => i.is_waiting);
                                        return (
                                            <div key={r} className="space-y-2" data-testid={`round-block-${t.id}-${r}`}>
                                                <div className={`flex items-center justify-between px-2 py-1 rounded ${
                                                    isWaitingRound
                                                        ? "bg-red-900/40 border border-red-700 text-red-200"
                                                        : (r === t.active_round
                                                            ? "bg-purple-900/40 border border-purple-700 text-purple-200"
                                                            : "bg-surface-elevated border border-[#1f2c44] text-elegant")
                                                }`}>
                                                    <div className="text-[11px] font-bold uppercase tracking-[0.2em]">Uscita {r}</div>
                                                    {isWaitingRound && (
                                                        <div className="text-[10px] font-bold uppercase tracking-wider animate-pulse" data-testid={`waiting-${t.id}-${r}`}>
                                                            ⛔ ATTENDERE
                                                        </div>
                                                    )}
                                                    {!isWaitingRound && r === t.active_round && (
                                                        <div className="text-[10px] font-bold uppercase tracking-wider">▶ IN CORSO</div>
                                                    )}
                                                </div>
                                                {roundItems.map((it) => (
                                                    <div key={it.id} className={`p-3 rounded-md border transition-colors ${
                                                        it.is_waiting ? "bg-red-950/40 border-red-800 opacity-80" :
                                                        it.status === "consegnato" ? "bg-brand/15 border-brand opacity-80" :
                                                        it.status === "pronto" ? "bg-green-900/20 border-green-700/50" :
                                                        it.status === "in_preparazione" ? "bg-amber-900/20 border-amber-700/50" :
                                                        it.status === "in_cucina" ? "bg-green-700/15 border-green-600 ring-1 ring-green-500/30" :
                                                        "bg-surface-elevated border-[#1f2c44]"
                                                    }`} data-testid={`item-${it.id}`}>
                                                        <div className="flex justify-between items-start gap-2">
                                                            <div className="flex-1">
                                                                <div className={`font-display font-bold flex items-center gap-2 ${it.is_waiting ? "text-red-200" : ""}`}>
                                                                    <span className="bg-brand text-deep px-1.5 py-0.5 rounded text-xs font-bold">×{it.quantity}</span>
                                                                    {it.name}
                                                                </div>
                                                                <div className="text-[10px] uppercase tracking-wider text-brand mt-1">{it.course}</div>
                                                                {!it.is_waiting && it.sent_at && it.status !== "pronto" && it.status !== "consegnato" && (
                                                                    <div className="text-[10px] text-elegant mt-1">in attesa da {elapsed(it.sent_at)}</div>
                                                                )}
                                                                {it.notes && <div className="text-xs italic text-amber-300 mt-1">📝 {it.notes}</div>}
                                                                {it.extras?.length > 0 && (
                                                                    <div className="mt-1 space-y-0.5" data-testid={`kds-extras-${it.id}`}>
                                                                        {it.extras.map((ex) => (
                                                                            <div key={ex.id} className="text-xs text-cyan-300 font-bold flex items-center gap-1">
                                                                                <span>+</span>
                                                                                <span>{ex.label}</span>
                                                                                {ex.price > 0 && <span className="text-cyan-400/80">· +€ {parseFloat(ex.price).toFixed(2)}</span>}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {it.allergens?.length > 0 && (
                                                                    <div className="flex items-center gap-1 mt-1 text-xs text-red-400 font-bold">
                                                                        <AlertTriangle className="w-3 h-3" /> {it.allergens.join(", ")}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 mt-2">
                                                            {it.is_waiting ? (
                                                                <div className="flex-1 h-12 bg-red-900/50 border-2 border-red-700 text-red-200 font-bold uppercase tracking-wider text-[11px] rounded flex items-center justify-center gap-2" data-testid={`wait-badge-${it.id}`}>
                                                                    ⛔ Attendere uscita {t.active_round}
                                                                </div>
                                                            ) : mode !== "video_only" ? (
                                                                <div className="flex-1 h-12 bg-deep border border-orange-500/30 text-orange-200 font-bold uppercase tracking-wider text-[10px] rounded flex items-center justify-center gap-2 px-2" data-testid={`mode-readonly-${it.id}`}>
                                                                    <Printer className="w-3.5 h-3.5" />
                                                                    {it.auto_archive_at ? (
                                                                        <>
                                                                            <span className="opacity-70">Si archivia tra</span>
                                                                            <span className="font-mono text-orange-300 text-sm" data-testid={`countdown-${it.id}`}>{countdown(it.auto_archive_at)}</span>
                                                                        </>
                                                                    ) : (
                                                                        <>Auto-stampa · solo consultazione</>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    {(it.status === "in_attesa" || it.status === "in_cucina") && (
                                                                        <button onClick={() => setStatus(t.id, it.id, "in_preparazione")} data-testid={`start-${it.id}`}
                                                                            className="flex-1 h-12 bg-green-600 hover:bg-green-500 text-white font-bold uppercase tracking-wider text-xs rounded flex items-center justify-center gap-1.5 shadow-lg shadow-green-900/30 animate-pulse">
                                                                            <Play className="w-3.5 h-3.5" /> Accetta comanda
                                                                        </button>
                                                                    )}
                                                                    {it.status === "in_preparazione" && (
                                                                        <button onClick={() => setStatus(t.id, it.id, "pronto")} data-testid={`ready-${it.id}`}
                                                                            className="flex-1 h-12 bg-amber-600 hover:bg-amber-500 text-white font-bold uppercase tracking-wider text-[11px] leading-tight rounded flex flex-col items-center justify-center gap-0.5 px-2">
                                                                            <span className="flex items-center gap-1.5"><ChefHat className="w-3.5 h-3.5" /> In preparazione</span>
                                                                            <span className="text-[9px] opacity-90 normal-case">premere quando pronto</span>
                                                                        </button>
                                                                    )}
                                                                    {it.status === "pronto" && (
                                                                        <button onClick={() => setStatus(t.id, it.id, "consegnato")} data-testid={`pickup-${it.id}`}
                                                                            className="flex-1 h-12 bg-brand hover:bg-brand-light text-deep font-bold uppercase tracking-wider text-[11px] leading-tight rounded flex flex-col items-center justify-center gap-0.5 px-2">
                                                                            <span className="flex items-center gap-1.5"><Hand className="w-3.5 h-3.5" /> Da portare al tavolo</span>
                                                                            <span className="text-[9px] opacity-90 normal-case">premere quando si preleva</span>
                                                                        </button>
                                                                    )}
                                                                    {it.status === "consegnato" && (
                                                                        <div data-testid={`delivered-${it.id}`}
                                                                            className="flex-1 h-12 bg-brand/20 border border-brand text-brand font-bold uppercase tracking-wider text-[11px] leading-tight rounded flex flex-col items-center justify-center gap-0.5 px-2 animate-pulse">
                                                                            <span className="flex items-center gap-1.5">✓ Consegnato al tavolo</span>
                                                                            <span className="text-[9px] opacity-80 normal-case">sparirà tra pochi secondi…</span>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                            {/* ⚠️ Tasto EVASA — solo in modalità mixed.
                                Marca la comanda evasa → cancellazione automatica dopo 30s
                                + notifica audio al tablet del cameriere "Piatto da ritirare". */}
                            {mode === "mixed" && (
                                <div className="px-4 pb-4 pt-2 border-t border-[#1f2c44]">
                                    <button
                                        onClick={() => markEvasa(t)}
                                        data-testid={`evasa-${t.id}`}
                                        className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-bold uppercase tracking-wider text-sm rounded flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/30"
                                        title="Marca tutti i piatti come evasi: avvisa il cameriere e fa sparire la tile tra 30s"
                                    >
                                        <CheckCircle2 className="w-5 h-5" /> EVASA — Avvisa cameriere
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TerminaleCucina;
